import { apiClient, unwrap, unwrapPaginated } from './client';
import type { Contact, ContactType, Opportunity, PipelineStage } from '@/types';

export interface ContactQuery {
  contactType?: ContactType;
  search?:      string;
  pays?:        string;
  page?:        number;
  limit?:       number;
}

export interface CreateContactPayload {
  contactType:  ContactType;
  fullName:     string;
  companyName?: string;
  email?:       string;
  phone?:       string;
  address?:     string;
  pays?:        string;
  // Ticket SIMGROUP-2026-003 §3.2 — mentions légales client OHADA
  rccmClient?:  string;
  nccClient?:   string;
}

/** TICKET-004 (C-01 + C-02) : modification d'une fiche contact, type compris. Chaîne vide = champ vidé. */
export type UpdateContactPayload = Partial<CreateContactPayload>;

export interface PipelineQuery {
  stage?:        PipelineStage;
  assignedToId?: string;
  page?:         number;
  limit?:        number;
}

export interface CreateOpportunityPayload {
  contactId:          string;
  title:              string;
  amountCents:        number;
  currency?:          string;
  stage?:             PipelineStage;
  probabilityPct?:    number;
  expectedCloseDate?: string;
  notes?:             string;
}

export interface UpdateOpportunityPayload {
  stage?:             PipelineStage;
  probabilityPct?:    number;
  amountCents?:       number;
  expectedCloseDate?: string;
  notes?:             string;
}

export const crmApi = {
  // Contacts
  getContacts: async (query: ContactQuery = {}) => {
    const res = await apiClient.get('/crm/contacts', { params: query });
    return unwrapPaginated<Contact>(res);
  },

  createContact: async (payload: CreateContactPayload): Promise<Contact> => {
    const res = await apiClient.post('/crm/contacts', payload);
    return unwrap<Contact>(res);
  },

  getContact: async (id: string): Promise<Contact> => unwrap<Contact>(await apiClient.get(`/crm/contacts/${id}`)),

  updateContact: async (id: string, payload: UpdateContactPayload): Promise<Contact> =>
    unwrap<Contact>(await apiClient.patch(`/crm/contacts/${id}`, payload)),

  // Pipeline
  getPipeline: async (query: PipelineQuery = {}) => {
    const res = await apiClient.get('/crm/pipeline', { params: query });
    return unwrapPaginated<Opportunity>(res);
  },

  createOpportunity: async (payload: CreateOpportunityPayload): Promise<Opportunity> => {
    const res = await apiClient.post('/crm/pipeline', payload);
    return unwrap<Opportunity>(res);
  },

  updateOpportunity: async (id: string, payload: UpdateOpportunityPayload): Promise<Opportunity> => {
    const res = await apiClient.patch(`/crm/pipeline/${id}`, payload);
    return unwrap<Opportunity>(res);
  },
};
